<template>
  <div
    id="example1"
    class="ht-theme-main full-width"
  >
    <div class="d-flex justify-space-between align-center mb-2">
      <div class="custom-input">
        <div>
          <router-link :to="goTo('data_logs')">
            <v-btn
              color="primary"
              text
              small
              outlined
            >
              <v-icon> mdi-arrow-left </v-icon>
            </v-btn>
          </router-link>
        </div>
        <div>
          <v-file-input
            class="file-input"
            label="Upload Excel File"
            counter
            show-size
            accept=".xlsx, .xls"
            hide-details
            @change="handleFileUpload"
          />
        </div>
      </div>
      <div class="d-flex gap5">
        <download-sample />
        <v-btn
          color="primary"
          small
          class="insert-button mr-2"
          @click="addEmptyRow"
        >
          <v-icon left>
            mdi-plus
          </v-icon>
          Add Row
        </v-btn>
      </div>
    </div>
    <hot-table
      v-if="!isFile"
      ref="hotTable"
      :settings="hotSettings"
    />
    <div class="d-flex justify-end mt-3">
      <v-btn
        color="primary"
        class="mx-0"
        :loading="isLoading"
        @click="saveData"
      >
        Save Logs
      </v-btn>
    </div>
  </div>
</template>

<script>
import { HotTable } from "@handsontable/vue";
import { registerAllModules } from "handsontable/registry";
import "handsontable/styles/handsontable.css";
import "handsontable/styles/ht-theme-main.css";
import moment from "moment";
import { mapGetters } from "vuex";
import * as XLSX from "xlsx"; // Import the xlsx library

registerAllModules();

export default {
  components: {
    HotTable,
    DownloadSample: () => import("../utils/DownloadSample.vue"),
  },
  data() {
    return {
      fileData: [],
      isFile: false,

      columns: [
        { key: "date", label: "Date" },
        { key: "bus_no", label: "Bus No" },
        { key: "filling_time", label: "Filling Time" },
        { key: "reg_no", label: "Reg No" },
        { key: "terminal", label: "Terminal" },
        { key: "dispenser", label: "Dispenser" },
        { key: "nozzle", label: "Nozzle" },
        { key: "total_diesel", label: "Total Diesel" },
        { key: "", label: "Actions" },
      ],
      dummyRows: [[]],
    };
  },
  computed: {
    ...mapGetters({
      authUser: "getUser",
      isLoading: "dataLogs/getLoading",
      terminalList: "terminal/getList",
      dispenserList: "fuelDispenser/getList",
    }),

    hotSettings() {
      const terminalOpt = this.terminalList?.map((r) => r.name);
      const fData = this.fileData.length > 0 ? this.fileData : this.dummyRows;
      // const dList = this.dispenserList?.map((r) => r.dispenser_identifier);
      const obj = {
        data: fData,
        colHeaders: this.columns.map((r) => r.label),
        rowHeaders: true,
        contextMenu: true,
        licenseKey: "non-commercial-and-evaluation",
        width: "100%",
        stretchH: "all",
        columns: [
          { type: "date", dateFormat: "YYYY-MM-DD", correctFormat: true },
          {}, // Bus No
          {}, // Filling Time
          {}, // Reg No
          { type: "dropdown", source: terminalOpt || [] }, // Terminal ID
          { type: "dropdown", source: [] }, // Dispenser ID
          { type: "dropdown", source: [1, 2] }, // Nozzle ID dropdown
          {}, // Total Diesel
          {
            renderer: function (instance, td, row) {
              td.innerHTML = `<v-btn color="danger" class="delete-btn">🗑️</v-btn>`;
              td.style.textAlign = "center";
              td.style.cursor = "pointer";
              td.querySelector("v-btn").onclick = function () {
                instance.alter("remove_row", row);
              };
              return td;
            },
          },
        ],
        cells: (row, col) => {
          const cellProperties = {};
          // Check if it's the Dispenser column (index 5)
          if (col === 5) {
            // Get the value of the Terminal cell in the current row (index 4)
            const terminalName = this.$refs.hotTable.hotInstance.getDataAtCell(
              row,
              4
            );
            if (terminalName) {
              const tRow = this.terminalList.find(
                (r) => r.name == terminalName
              );
              const dispenserOpt = this.dispenserList.filter(
                (r) => r.terminal_id == tRow?.id
              );
              cellProperties.source = dispenserOpt.map(
                (r) => r.dispenser_identifier
              );
            }
          }
          // for Nozzle
          // if (col === 6) {
          //   const dispenserName = this.$refs.hotTable.hotInstance.getDataAtCell(
          //     row,
          //     5
          //   );
          //   if (dispenserName) {
          //   }
          // }
          return cellProperties;
        },
      };
      return obj;
    },
  },
  mounted() {
    this.getTerminal();
    this.getDispenser();
  },
  methods: {
    addEmptyRow() {
      if (this.fileData.length > 0) {
        this.fileData.push([]);
      } else {
        this.dummyRows.push([]);
      }
    },
    getTerminal() {
      this.$store.dispatch("terminal/list");
    },
    getDispenser() {
      this.$store.dispatch("fuelDispenser/list");
    },

    handleFileUpload(event) {
      if (event) {
        const file = event;
        if (!file) return;
        this.isFile = true;
        const reader = new FileReader();
        reader.onload = (e) => {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array", cellDates: true }); // Crucial: cellDates: true
          // const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets["Data-logs"];

          // Use header: 1 to get an array of arrays, which is easier to process for headers
          let rawData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            raw: false,
            defval: null,
          });
          if (rawData.length > 1) {
            // Remove Headers from file
            if (rawData[0].every((r) => typeof r == "string")) {
              rawData.shift();
            }
            // Check if there's at least a header and one row of data
            // const headers = rawData[0];
            const arr = rawData.filter((r) => r.every((c) => c !== null));
            // this.fileData = arr.map((r) => r.map((c, i) => (i == 0 ? c : c)));
            this.fileData = arr.map((row) => {
              const d = new Date(row[0]);
              row[0] = moment(d).format("YYYY-MM-DD");
              return row;
            });
          } else {
            this.fileData = [];
            this.$store.dispatch(
              "alerts/error",
              "The uploaded Excel file is not in required format"
            );
          }
        };
        reader.readAsArrayBuffer(file);
      } else {
        this.isFile = true;
        this.fileData = [];
      }

      setTimeout(() => {
        this.isFile = false;
      }, 150);
    },
    // Insert Data Log
    saveData() {
      const hotInstance = this.$refs.hotTable.hotInstance;
      const data = hotInstance.getData();
      const isNullArr = [];
      const params = {
        payload: [],
      };
      // for tenant
      if (this.authUser.isAdmin) {
        if (this.$route.query.tenant_id || this.$route.query.fleetId) {
          params.tenant_id =
            this.$route.query.tenant_id || this.$route.query.fleetId;
        }
      } else {
        params.tenant_id = this.authUser.independent_fleet_user_nature
          ? this.authUser.tenant_id
          : this.authUser.parent_tenant_id;
      }
      //
      const cols = this.columns.map((r) => r.key).filter((r) => r);
      data.forEach((el) => {
        el.length = cols.length;
        isNullArr.push(el.some((r) => r == null));

        const obj = {};
        cols.forEach((key, i) => {
          if (key == "terminal") {
            const tRow = this.terminalList.find((r) => r.name == el[i]);
            obj[key] = tRow?.id;
          } else if (key == "dispenser") {
            const dRow = this.dispenserList.find(
              (r) => r.dispenser_identifier == el[i]
            );
            obj[key] = dRow?.id;
          } else if (key == "nozzle") {
            const { nozzles } = this.dispenserList.find(
              (r) => r.id == obj["dispenser"]
            );
            const nRow = nozzles.find((r) => r.nozzle_number == el[i]);
            obj[key] = nRow?.id || 13;
          } else {
            obj[key] = el[i];
          }
        });
        params.payload.push(obj);
      });

      if (isNullArr.every((r) => !r)) {
        //
        this.$store.dispatch("dataLogs/createDataLogs", params).then((res) => {
          if (res?.status_code == 200) {
            this.$store.dispatch("alerts/success", "Save Successfully");
            this.$router.push({
              name: "data_logs",
              query: this.$route.query,
            });
          } else {
            const err = res?.response?.data?.message[0];
            const msg = `${Object.keys(err).join(", ")} are required`;
            this.$store.dispatch("alerts/error", msg);
          }
        });
        //
      } else {
        const msg = `Please ensure all Data fields are filled.`;
        this.$store.dispatch("alerts/error", msg);
      }
    },
    goTo(path) {
      return { name: path, query: this.$route.query };
    },
  },
};
</script>
<style scoped>
#example1 {
  width: 100%;
}
.custom-input {
  display: flex;
  align-items: baseline;
  flex-basis: 30%;
}
.custom-input div:last-child {
  flex: 1;
}
.delete-btn {
  background-color: #e74c3c;
  border: none;
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
}
</style>
